import { captureException } from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { logError } from '@/lib/errorLogging';

interface TerminalErrorBoundaryProps {
  /**
   * Called after the error is reported. The panel removes the owning tab, which
   * unmounts this boundary - the only way a class boundary leaves `failed`.
   */
  onError: () => void;
  children: ReactNode;
}

interface TerminalErrorBoundaryState {
  failed: boolean;
}

/**
 * Keeps a terminal render/mount failure (addon incompatibility, WebGL init, a
 * broken theme token) inside the dock. Without this the nearest boundary is the
 * router's whole-screen ErrorScreen, which replaces the entire app.
 */
export class TerminalErrorBoundary extends Component<
  TerminalErrorBoundaryProps,
  TerminalErrorBoundaryState
> {
  state: TerminalErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): TerminalErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logError(error, 'Terminal crashed', {
      context: 'TerminalErrorBoundary',
      metadata: { componentStack: info.componentStack },
    });
    captureException(error);
    this.props.onError();
  }

  render(): ReactNode {
    // onError removes the tab, so nothing is shown in the failed state
    return this.state.failed ? null : this.props.children;
  }
}
